import { useCallback, useEffect, useState } from 'react';

/**
 * A generic hook that wraps an async API call with loading, error, and retry functionality.
 * Reduces boilerplate for hooks that fetch data from APIs.
 *
 * @template T - The type of data returned by the callback
 * @param callback - Async function that fetches the data
 * @returns Object containing:
 *   - isLoading: Whether the request is in progress
 *   - error: Error message if the request failed, undefined otherwise
 *   - value: The fetched data, undefined until loaded
 *   - retry: Function to re-fetch the data
 *
 * @example
 * const { isLoading, error, value, retry } = useWrappedApi(() => fetchUserData(userId));
 */
const useWrappedApi = <T>(callback: () => Promise<T>) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [value, setValue] = useState<T | undefined>(undefined);

  const fetchValueAsync = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(undefined);
      setValue(await callback());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  }, [callback]);

  useEffect(() => {
    void fetchValueAsync();
  }, [fetchValueAsync]);

  return {
    isLoading,
    error,
    value,
    retry: fetchValueAsync,
  };
};

export default useWrappedApi;
